/** @file Error handling utilities for API and network errors. */
import * as api from '@codegrade/api';
import * as utils from '@codegrade/utils';
import { isApiError } from './api';
/**
 * Known browser-specific error messages thrown by `fetch` on network failure.
 *
 * The `fetch` API throws a plain `TypeError` for network errors (DNS failure,
 * connection refused, offline, etc.) with a browser-dependent message string.
 * There is no dedicated error type — this is by spec to avoid leaking local
 * network details.
 */
const NETWORK_ERROR_MESSAGES = Object.freeze([
    'Failed to fetch', // Chrome
    'NetworkError when attempting to fetch resource', // Firefox
    'Load failed', // Safari
]);
/**
 * Check if an error is a network failure thrown by the `fetch` API.
 *
 * Browser `fetch` throws a generic `TypeError` for network-level failures.
 * This function identifies those by matching known browser-specific message
 * strings.
 *
 * @param err The error to check.
 * @returns Whether the error appears to be a fetch network failure.
 */
export function isNetworkError(err) {
    return (err instanceof TypeError &&
        NETWORK_ERROR_MESSAGES.some(msg => err.message.toLowerCase().includes(msg.toLowerCase())));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class AggregateError extends Error {
    errors;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(errors) {
        super('Multiple errors occurred');
        this.errors = errors;
    }
}
/**
 * Get the error message from an error(-like) objects.
 *
 * @param err The error to get the message from.
 * @returns The error message, or a generic message if we could not find one.
 */
export function getErrorMessage(err) {
    let msg;
    if (err == null) {
        // Defensive: the type signature disallows nullish input, but this
        // helper is also called from loose-typed `.vue` files (plain JS, no
        // strict `unknown` catch-vars) and from reactive computeds that read
        // nullable error state (e.g. `SubmitButton.stringifiedError` reads
        // `this.error` which initialises as `null`). Returning early avoids a
        // `TypeError` from the `'message' in err` check below — an error
        // formatter that throws would break the very UI trying to surface the
        // error.
        return '';
    }
    if (err instanceof api.BaseError && err.message != null) {
        return err.message;
    }
    else if (err instanceof api.ApiResultError && err.error?.message != null) {
        return err.error.message;
    }
    else if (err instanceof Error) {
        msg = err.message;
    }
    else if (Object.hasOwn(err, 'message') &&
        'message' in err &&
        typeof err.message === 'string') {
        msg = err.message;
    }
    else {
        // TODO: Find out why we have this case, and eliminate its use by just
        // throwing errors.
        msg = err.toString();
    }
    return msg ?? 'Something unknown went wrong';
}
/**
 * Parse the response body of an {@link api.ApiResultError} to extract
 * the server's error message.
 *
 * This only matters for `client.rawRequest`: api-core's `baseResponses`
 * wraps 4xx/5xx statuses in a generic `Error` (e.g. "Internal Server
 * Error") without reading the body. Generated `client.p.*` methods go
 * through `makeResponses` in `@codegrade/api`, which already decodes
 * the body as an `AnyError` and exposes the server's `message` on
 * `err.error.message` — no body-parsing is required for those.
 *
 * @param err The ApiResultError whose response body to parse.
 * @returns An Error with the server's message, or the original error
 *          if the body could not be parsed.
 */
async function parseResponseError(err) {
    try {
        const body = await err.response.clone().json();
        if (body != null &&
            typeof body === 'object' &&
            Object.hasOwn(body, 'message') &&
            'message' in body &&
            typeof body.message === 'string') {
            return new Error(body.message);
        }
    }
    catch {
        // Body is not JSON or already consumed — fall through.
    }
    return err;
}
/**
 * Wrap a promise so that {@link api.ApiResultError} rejections are
 * re-thrown with the server's error message parsed from the response
 * body.
 *
 * Use this around `client.rawRequest` or any `@codegrade/api` call
 * where you need {@link getErrorMessage} to return the server's
 * message rather than a generic HTTP status description.
 *
 * @param promise The promise to wrap.
 * @returns The resolved value of the promise.
 */
export async function withParsedApiErrors(promise) {
    try {
        return await promise;
    }
    catch (err) {
        if (err instanceof api.ApiResultError) {
            throw await parseResponseError(err);
        }
        throw err;
    }
}
/**
 * Build a `client.rawRequest` response handler for an HTTP error status that
 * parses the JSON body as an {@link api.AnyError} and throws an
 * {@link api.ApiResultError} carrying that typed body.
 *
 * Generated `client.p.*` methods already do this via `makeResponses` in
 * `@codegrade/api` (they merge typed `AnyError` decoders for 400/401/403/
 * 404/409/429/500 into every call). This helper is only needed for
 * `client.rawRequest`, whose defaults fall through to api-core's
 * `baseResponses` — those produce generic messages (e.g. "Bad Request",
 * "Not Found") without reading the body, hiding the backend's `message`
 * field and specialized subclasses like {@link api.WeakPasswordException}.
 *
 * @param fallbackMessage Message used when the body cannot be decoded as a
 * known `AnyError` shape.
 * @returns A response parser that always throws an `ApiResultError`.
 */
export function parseApiErrorBody(fallbackMessage) {
    return async (response) => {
        const body = await response.json().catch(() => null);
        const errValue = api.AnyError.decode(body)
            .mapLeft(() => new Error(fallbackMessage))
            .extract();
        throw api.ApiResultError.fromResponse(errValue, response);
    };
}
/**
 * Response handlers for the password-reset endpoint
 * (`PATCH /api/v1/login?type=reset_password`).
 *
 * The server returns:
 *   - 400 on validation failures. Weak-password errors deserialize into
 *     {@link api.WeakPasswordException} so downstream handlers can recover.
 *   - 403 for invalid or revoked reset tokens.
 *   - 404 for unknown users or reset requests.
 *
 * When the body is a decodable {@link api.AnyError}, its `message` is
 * surfaced. The sentences below are only rendered when the body cannot
 * be decoded at all.
 *
 * @returns A record of parsers keyed by status code, ready to spread into
 *          a `client.rawRequest` `responses` object.
 */
export function passwordResetErrorResponses() {
    return {
        400: parseApiErrorBody('Your password reset request was rejected.'),
        403: parseApiErrorBody('You are not allowed to reset this password.'),
        404: parseApiErrorBody('This password reset link is invalid or has expired.'),
    };
}
// We do not need consistent return, because we always throw if the status code
// is invalid.
/* eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation. */
function statusPattern(status) {
    if (status >= 100) {
        if (status < 200) {
            return '1xx';
        }
        else if (status < 300) {
            return '2xx';
        }
        else if (status < 400) {
            return '3xx';
        }
        else if (status < 500) {
            return '4xx';
        }
        else if (status < 600) {
            return '5xx';
        }
    }
    return utils.assert.AssertionError.assert(false, 'HTTP status code must be in the range [100, 600)');
}
/**
 * The actual logic of the `tryHandleApiError` function.
 *
 * @param handlers See documentation on `ApiErrorHandlers` type.
 * @param err The error we are handling.
 * @returns The type results from the handlers.
 */
function innerHandleApiError(handlers, err) {
    if (isApiError(err)) {
        const status = err.response.status;
        const exactHandler = handlers[status];
        if (exactHandler != null) {
            return exactHandler(err);
        }
        const patternHandler = handlers[statusPattern(status)];
        if (patternHandler != null) {
            return patternHandler(err);
        }
        const defaultHandler = handlers.default;
        if (defaultHandler != null) {
            return defaultHandler(err);
        }
    }
    if (!(err instanceof api.ApiResultError) && handlers.noResponse != null) {
        return handlers.noResponse(utils.errors.coerceToError(err));
    }
    return handlers._(utils.errors.coerceToError(err));
}
/**
 * A currying function that try handle a provided error using a map with `ApiErrorHandlers` type.
 *
 * @param handlers See documentation on `ApiErrorHandlers` type.
 * @param err The error to be handled.
 * @returns The result, or the function itself with arguments curried,
 *          depending on the amount of incoming arguments.
 */
export function tryHandleApiError(handlers, err) {
    switch (arguments.length) {
        case 0:
            return tryHandleApiError;
        case 1:
            return (correctErr) => innerHandleApiError(handlers, correctErr);
        default:
            return innerHandleApiError(handlers, err);
    }
}
